import { useState, type FormEvent } from "react";
import { istBetrieb } from "./konsole-nav";
import { Link, useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, type Api } from "../../data/api";
import { api } from "../../data";
import { useSwitchStaffOrg } from "../../app/session";
import { Dialog, PillView, useAction, useRueckfrage } from "../../components/ui";
import { date, time } from "../../lib/format";
import { Fehler, Laden } from "./parts";
import { pille } from "./konsole-model";
import { useKonsolenRolle } from "./rolle";

/**
 * Demo-Konto (Vertrieb-Konsole, Inhaber 02.11.2026, Migration 0461): Der Vertrieb ist Mitglied (Manager) des markierten
 * Demo-Kontos, öffnet es über „Demo öffnen“ im echten Portal und stellt danach mit „Demo zurücksetzen“ den gespeicherten
 * Ausgangsstand her. Den Ausgangsstand speichert ein Admin; Mails aus dem Demo-Konto werden nie versendet.
 */

export interface DemoKonto {
  org_id: string; name: string; status: string; marked_at: string | null; snapshot_at: string | null; snapshot_rows: number; snapshot_by: string | null;
  reset_at: string | null; reset_by: string | null; reset_counts: { geloescht: number; wiederhergestellt: number; zurueckgesetzt: number } | null;
  member: boolean; zugaenge: number;
}
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
export const DEMO_STATUS_KEY = ["platform_demo_status", "betrieb"] as const;

export async function loadDemoStatus(a: Pick<Api, "read">): Promise<DemoKonto[]> {
  return parse<DemoKonto[] | null>(await a.read("platform_demo_status", {})) ?? [];
}
export async function setDemoAccount(a: Pick<Api, "rpc">, orgId: string, on: boolean, name: string | null): Promise<{ is_demo: boolean; zugang?: number; zugang_fehlt?: number; fehler?: { person: string; meldung: string }[] }> {
  if (on && !name?.trim()) throw new ApiError("Zur Bestätigung bitte den Namen des Kontos eingeben.");
  return parse(await a.rpc("platform_demo_account_set", { p_org: orgId, p_on: on, p_confirm_name: name?.trim() || null }));
}
export async function saveDemoSnapshot(a: Pick<Api, "rpc">, orgId: string): Promise<{ tables: number; rows: number }> {
  return parse(await a.rpc("platform_demo_snapshot_save", { p_org: orgId }));
}
export async function resetDemo(a: Pick<Api, "rpc">, orgId: string): Promise<{ geloescht: number; wiederhergestellt: number; zurueckgesetzt: number }> {
  return parse(await a.rpc("platform_demo_reset", { p_org: orgId }));
}
export async function joinDemo(a: Pick<Api, "rpc">, orgId: string): Promise<void> {
  await a.rpc("platform_demo_join", { p_org: orgId });
}
export const useDemoStatus = () => useQuery({ queryKey: DEMO_STATUS_KEY, queryFn: () => loadDemoStatus(api) });

export function resetText(r: { geloescht: number; wiederhergestellt: number; zurueckgesetzt: number }): string {
  // Prüf-Chat Nachtest (03.11.2026), Befund 9: Einzahl und Mehrzahl („1 neuer Eintrag entfernt“)
  const teile = [r.geloescht ? (r.geloescht === 1 ? "1 neuer Eintrag entfernt" : `${r.geloescht} neue Einträge entfernt`) : null,
    r.wiederhergestellt ? (r.wiederhergestellt === 1 ? "1 gelöschter Eintrag wiederhergestellt" : `${r.wiederhergestellt} gelöschte wiederhergestellt`) : null,
    r.zurueckgesetzt ? (r.zurueckgesetzt === 1 ? "1 geänderter Eintrag zurückgesetzt" : `${r.zurueckgesetzt} geänderte zurückgesetzt`) : null].filter(Boolean);
  return teile.length ? `${teile.join(", ")}.` : "Es war nichts zu tun; das Demo-Konto war schon im Ausgangsstand.";
}

/**
 * „Demo öffnen“: das Demo-Konto im Kundenportal zeigen, mit ihm als gewählter Agentur. Seit dem internen Bereich (Inhaber
 * 03.11.2026) hat das Kundenportal eine eigene Sitzung: Es öffnet in einem neuen Tab, dort meldest du dich (einmal) mit derselben
 * Adresse an. Die Konsole bleibt in diesem Tab angemeldet. Demo im Browser: ein Anschluss für alles, daher wie bisher im selben Tab.
 */
export function useDemoOeffnen() {
  const wechseln = useSwitchStaffOrg();
  const navigate = useNavigate();
  const qc = useQueryClient();
  return async (orgId: string) => {
    await qc.refetchQueries({ queryKey: ["account"] });
    wechseln(orgId);
    if (api.mode === "demo") navigate("/agentur");
    else window.open("/agentur", "_blank", "noopener");
  };
}

export function DemoKontoSeite() {
  const q = useDemoStatus();
  const kopf = <><title>Demo-Konto · Konsole · CreatorOffice</title>
    <div className="pagehead"><div><h1>Demo-Konto</h1><p className="muted">Im echten Portal zeigen, danach auf den Ausgangsstand zurücksetzen. Aus dem Demo-Konto gehen keine Mails hinaus.</p></div></div></>;
  if (q.error) return <>{kopf}<Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending) return <>{kopf}<Laden /></>;
  return (
    <>
      {kopf}
      {(q.data ?? []).length === 0
        ? <div className="panel stack tk-empty"><p>Noch kein Demo-Konto. Ein Admin markiert eine eigene Agentur in ihrer Kundenakte als Demo-Konto (Block „Demo-Konto“) und speichert hier den Ausgangsstand.</p></div>
        : <div className="grid tk-crm-kundenakte">{(q.data ?? []).map((d) => <DemoKarte key={d.org_id} d={d} />)}</div>}
      <p className="source-note">Zurückgesetzt werden die Arbeitsdaten der Agentur (Creator, Marken, Kooperationen, Verträge, Rechnungen, Zahlungen, Content, Anfragen,
        Kalender, Nachrichten, Glocke, Einstellungen und Vorlagen). Nicht zurückgesetzt: Mitglieder, Abo und Lebenszyklus, Support, Protokolle, Instagram und WhatsApp;
        hochgeladene Dateien bleiben im Speicher liegen.</p>
    </>
  );
}

export function DemoKarte({ d, kompakt }: { d: DemoKonto; kompakt?: boolean }) {
  const admin = istBetrieb(useKonsolenRolle());
  const { busy, error, run } = useAction();
  const frage = useRueckfrage();
  const oeffnen = useDemoOeffnen();
  const [ergebnis, setErgebnis] = useState<string | null>(null);
  async function zuruecksetzen() {
    if (!(await frage.ask({ title: "Demo zurücksetzen?", confirmLabel: "Zurücksetzen", danger: true,
      text: `Alles, was seit dem Ausgangsstand vom ${d.snapshot_at ? date(d.snapshot_at) : "–"} im Demo-Konto „${d.name}“ angelegt, geändert oder gelöscht wurde, kehrt zum Ausgangsstand zurück. Das gilt für alle, die gerade im Demo-Konto arbeiten.` }))) return;
    const r = await run("zurueck", () => resetDemo(api, d.org_id), "Demo-Konto zurückgesetzt.");
    if (r) setErgebnis(resetText(r));
  }
  async function speichern() {
    if (!(await frage.ask({ title: "Ausgangsstand speichern?", confirmLabel: "Speichern",
      text: `Der jetzige Stand von „${d.name}“ wird zum neuen Ausgangsstand. „Demo zurücksetzen“ kehrt danach immer zu diesem Stand zurück.` }))) return;
    const r = await run("speichern", () => saveDemoSnapshot(api, d.org_id), "Ausgangsstand gespeichert.");
    if (r) setErgebnis(`Ausgangsstand mit ${r.rows} Einträgen aus ${r.tables} Tabellen gespeichert.`);
  }
  return (
    <section className="panel stack" aria-labelledby={`demo-${d.org_id}`}>
      <div className="row between"><h2 id={`demo-${d.org_id}`}>{d.name}</h2><PillView pill={pille("Demo-Konto", "info")} /></div>
      {!kompakt && (
        <dl className="tk-crm-fakten">
          <div><dt>Ausgangsstand</dt><dd>{d.snapshot_at ? `${date(d.snapshot_at)} ${time(d.snapshot_at)} · ${d.snapshot_rows} Einträge${d.snapshot_by ? ` · ${d.snapshot_by}` : ""}` : "noch nicht gespeichert"}</dd></div>
          <div><dt>Zuletzt zurückgesetzt</dt><dd>{d.reset_at ? `${date(d.reset_at)} ${time(d.reset_at)}${d.reset_by ? ` · ${d.reset_by}` : ""}` : "noch nie"}</dd></div>
          <div><dt>Zugang</dt><dd>{d.member ? "du bist Mitglied (Manager)" : "du bist noch kein Mitglied"} · {d.zugaenge} {d.zugaenge === 1 ? "Zugang" : "Zugänge"} über die Konsole</dd></div>
        </dl>
      )}
      <div className="row tk-crm-aktionen">
        {d.member
          ? <button type="button" className="primary" onClick={() => void oeffnen(d.org_id)}>Demo öffnen</button>
          : <button type="button" disabled={!!busy} aria-busy={busy === "beitreten"} onClick={() => void run("beitreten", () => joinDemo(api, d.org_id), "Zugang angelegt. Die Demo lässt sich jetzt öffnen.")}>Mir Zugang geben</button>}
        <button type="button" className="danger" disabled={!!busy || !d.snapshot_at} aria-busy={busy === "zurueck"} onClick={() => void zuruecksetzen()}>
          {busy === "zurueck" ? "Wird zurückgesetzt …" : "Demo zurücksetzen"}
        </button>
        {admin && <button type="button" disabled={!!busy} aria-busy={busy === "speichern"} onClick={() => void speichern()}>Ausgangsstand speichern</button>}
        {kompakt && <Link to="/intern/demo">Details</Link>}
      </div>
      {d.member && <p className="muted"><small>„Demo öffnen“ öffnet das Kundenportal in einem neuen Tab; dort meldest du dich mit derselben Adresse an.</small></p>}
      {!d.snapshot_at && <p className="muted"><small>Zurücksetzen geht, sobald ein Admin den Ausgangsstand gespeichert hat.</small></p>}
      {ergebnis && <p role="status">{ergebnis}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      {frage.element}
    </section>
  );
}

/** Admin-Block der Kundenakte: Konto als Demo-Konto markieren oder die Markierung aufheben */
export function DemoMarkierung({ orgId, name }: { orgId: string; name: string }) {
  const q = useDemoStatus();
  const { busy, error, run } = useAction();
  const frage = useRueckfrage();
  const [offen, setOffen] = useState(false);
  const [eingabe, setEingabe] = useState("");
  const [hinweis, setHinweis] = useState<string | null>(null);
  const istDemo = !!q.data?.some((d) => d.org_id === orgId);
  async function markieren(e: FormEvent) {
    e.preventDefault();
    const r = await run("demo", () => setDemoAccount(api, orgId, true, eingabe), "Als Demo-Konto markiert.");
    if (r) { setOffen(false); setEingabe(""); }
    // 0501: Demo-Zugänge belegen keinen Platz mehr; scheitert ein Zugang trotzdem, steht die Meldung der Datenbank hier
    setHinweis(r && r.zugang_fehlt ? `${r.zugang_fehlt} ${r.zugang_fehlt === 1 ? "Person im Vertrieb hat" : "Personen im Vertrieb haben"} noch keinen Zugang: ${(r.fehler ?? []).map((f) => f.meldung).join(" · ") || "Grund unbekannt"}. Bitte prüfen und die Markierung neu setzen.` : null);
  }
  return (
    <section className="panel stack" aria-labelledby="demo-markierung">
      <h2 id="demo-markierung">Demo-Konto</h2>
      {q.isPending ? <Laden /> : istDemo ? (
        <>
          <p>Dieses Konto ist ein Demo-Konto: Der Vertrieb ist Mitglied, Mails werden nie versendet, „Demo zurücksetzen“ stellt den Ausgangsstand her.</p>
          <div className="row tk-crm-aktionen">
            <Link className="btn" to="/intern/demo">Zum Demo-Konto</Link>
            <button type="button" disabled={!!busy} onClick={() => void (async () => {
              if (await frage.ask({ title: "Markierung aufheben?", danger: true, confirmLabel: "Aufheben",
                text: "Der Vertrieb verliert den Zugang über die Konsole, der Ausgangsstand wird gelöscht und Mails des Kontos gehen wieder hinaus." })) {
                await run("demo-aus", () => setDemoAccount(api, orgId, false, null), "Markierung aufgehoben.");
              }
            })()}>Markierung aufheben</button>
          </div>
        </>
      ) : (
        <>
          <p className="muted">Nur für eigene Konten ohne Zahlungsdienst: Der Vertrieb wird Mitglied (Manager), alle Mails des Kontos werden unterdrückt und der Vertrieb kann es auf einen gespeicherten Ausgangsstand zurücksetzen.</p>
          <div className="row"><button type="button" onClick={() => setOffen(true)}>Als Demo-Konto markieren</button></div>
        </>
      )}
      {hinweis && <p className="notice warning" role="alert">{hinweis}</p>}
      {error && <p className="tk-error" role="alert">{error}</p>}
      {frage.element}
      <Dialog open={offen} onClose={() => setOffen(false)} title="Als Demo-Konto markieren">
        <form className="stack" onSubmit={(e) => void markieren(e)}>
          <p>Zur Bestätigung den Namen des Kontos eingeben: <strong>{name}</strong></p>
          <label>Name des Kontos<input value={eingabe} onChange={(e) => setEingabe(e.target.value)} autoComplete="off" /></label>
          <div className="row">
            <button type="submit" className="primary" disabled={!!busy || eingabe.trim().toLowerCase() !== name.trim().toLowerCase()} aria-busy={busy === "demo"}>Markieren</button>
            <button type="button" onClick={() => setOffen(false)}>Abbrechen</button>
          </div>
        </form>
      </Dialog>
    </section>
  );
}
